/**
 * VideoTemplateFilters
 *
 * Responsive multi-select filter bar and sorting controls for video templates,
 * with portals for dropdowns and mobile categories mode.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Star, ChevronDown, Clock, Shuffle, X } from 'lucide-react';
import { VIDEO_TEMPLATE_FORMAT_LIST } from '../../../data/tags/tagEnums';
import { useToast } from '../../ui/notifications/toast-notification.jsx';

const VISIBLE_TAGS_COUNT = 8; // Default number of visible tags
const MAX_ACTIVE_FILTERS = 4; // Define the limit

function VideoTemplateFilters({
  activeFilter,
  setActiveFilter,
  sortMethod,
  setSortMethod,
  searchLoading,
  isLoggedIn = true,
  isBasicUser = false,
  isPremiumUser = true,
  showSavedVideoTemplates = false,
  setShowSavedVideoTemplates
}) {
  const { addToast } = useToast?.() || { addToast: () => {} };
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const [showCategoriesButton, setShowCategoriesButton] = useState(false);

  // --- State ---
  const [maxVisibleTags, setMaxVisibleTags] = useState(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 640) return 0;
    return VISIBLE_TAGS_COUNT;
  });
  
  const [activeFilters, setActiveFilters] = useState(() => {
    const initialActive = Array.isArray(activeFilter) ? activeFilter : (activeFilter ? [activeFilter] : []);
    return initialActive.filter(tag => VIDEO_TEMPLATE_FORMAT_LIST.includes(tag));
  });
  
  const [visibleTags, setVisibleTags] = useState([]);
  const [hiddenTags, setHiddenTags] = useState([]);

  // Refs
  const categoriesMenuRef = useRef(null);
  const moreMenuRef = useRef(null);
  const dropdownRef = useRef(null);
  const sortMenuRef = useRef(null);
  const sortDropdownRef = useRef(null);
  const filtersContainerRef = useRef(null);
  const tagsContainerRef = useRef(null);
  const controlsRef = useRef(null);

  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [sortDropdownPosition, setSortDropdownPosition] = useState({ top: 0, left: 0 });

  // Convert single filter to array for consistency
  useEffect(() => {
    const newActiveFilters = Array.isArray(activeFilter) ? activeFilter : (activeFilter ? [activeFilter] : []);
    setActiveFilters(newActiveFilters.filter(tag => VIDEO_TEMPLATE_FORMAT_LIST.includes(tag)));
  }, [activeFilter]);

  // Propagate activeFilters changes upwards - supporting multiple filters
  useEffect(() => {
    const newFilterValue = activeFilters.length > 0 ? activeFilters : null;
    
    // Only call setActiveFilter if the value actually changed (avoid infinite loops)
    const currentFilterString = JSON.stringify(Array.isArray(activeFilter) ? activeFilter : (activeFilter ? [activeFilter] : []));
    const newFilterString = JSON.stringify(activeFilters);
    
    if (currentFilterString !== newFilterString) {
      setActiveFilter(newFilterValue);
    }
  }, [activeFilters, activeFilter]);

  // Update dropdown positions on scroll and window resize
  useEffect(() => {
    const updateDropdownPositions = () => {
      if (isMoreMenuOpen) {
        const buttonRef = window.innerWidth < 640 
          ? categoriesMenuRef.current 
          : moreMenuRef.current;
          
        if (buttonRef) {
          const rect = buttonRef.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom + window.scrollY,
            left: Math.min(rect.left + window.scrollX, window.innerWidth - 224)
          });
        }
      }
      if (isSortMenuOpen && sortMenuRef.current) {
        const rect = sortMenuRef.current.getBoundingClientRect();
        setSortDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: Math.min(rect.left + window.scrollX, window.innerWidth - 160)
        });
      }
    };
    
    if (isMoreMenuOpen || isSortMenuOpen) {
      window.addEventListener('scroll', updateDropdownPositions);
      window.addEventListener('resize', updateDropdownPositions);
      updateDropdownPositions();
    }
    
    return () => {
      window.removeEventListener('scroll', updateDropdownPositions);
      window.removeEventListener('resize', updateDropdownPositions);
    };
  }, [isMoreMenuOpen, isSortMenuOpen]);

  // Central function to calculate and set visible/hidden tags
  const balanceTags = useCallback((maxVisible, currentActiveFilters) => {
    if (maxVisible <= 0) {
      setVisibleTags([]);
      setHiddenTags(VIDEO_TEMPLATE_FORMAT_LIST);
      return;
    }

    // Ensure active filters are always visible
    const activeSet = new Set(currentActiveFilters);
    const inactiveTags = VIDEO_TEMPLATE_FORMAT_LIST.filter(tag => !activeSet.has(tag));
    
    // Calculate available slots for inactive tags
    const slotsForInactive = Math.max(0, maxVisible - currentActiveFilters.length);
    
    // Select inactive tags to show
    const visibleInactiveTags = inactiveTags.slice(0, slotsForInactive);
    
    // Combine active and visible inactive tags
    const newVisibleTags = [...currentActiveFilters, ...visibleInactiveTags];
    const newHiddenTags = inactiveTags.slice(slotsForInactive);
    
    setVisibleTags(newVisibleTags);
    setHiddenTags(newHiddenTags);
  }, []);

  // Responsive layout handler
  const handleResize = useCallback(() => {
    if (typeof window === 'undefined' || !filtersContainerRef.current || !tagsContainerRef.current || !controlsRef.current) return;

    const containerWidth = filtersContainerRef.current.clientWidth;
    const isMobileView = containerWidth < 640;
    const isIntermediateView = containerWidth >= 640 && containerWidth < 800;
    let calculatedMaxVisible;

    setShowCategoriesButton(isIntermediateView || isMobileView);

    if (isMobileView) {
      calculatedMaxVisible = 0;
    } else if (isIntermediateView) {
      calculatedMaxVisible = 1;
    } else {
      // Desktop View Calculation
      const controlsWidth = controlsRef.current.clientWidth + 20;
      const availableWidth = containerWidth - controlsWidth;
      const averageTagWidth = 120;

      let preliminaryMax = Math.max(1, Math.floor(availableWidth / averageTagWidth));
      preliminaryMax = Math.min(preliminaryMax, VIDEO_TEMPLATE_FORMAT_LIST.length);

      const needsMoreButton = VIDEO_TEMPLATE_FORMAT_LIST.length > preliminaryMax;
      const moreButtonWidth = needsMoreButton ? 100 : 0;

      calculatedMaxVisible = Math.max(1, Math.floor((availableWidth - moreButtonWidth) / averageTagWidth));
      calculatedMaxVisible = Math.min(calculatedMaxVisible, VIDEO_TEMPLATE_FORMAT_LIST.length);
    }

    if (calculatedMaxVisible !== maxVisibleTags) {
      setMaxVisibleTags(calculatedMaxVisible);
    }
  }, [maxVisibleTags]);

  // Set up resize observer and initial calculation
  useEffect(() => {
    balanceTags(maxVisibleTags, activeFilters);
  }, [maxVisibleTags, activeFilters, balanceTags]);

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && 
          moreMenuRef.current && !moreMenuRef.current.contains(event.target) &&
          categoriesMenuRef.current && !categoriesMenuRef.current.contains(event.target)) {
        setIsMoreMenuOpen(false);
      }
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target) && 
          sortMenuRef.current && !sortMenuRef.current.contains(event.target)) {
        setIsSortMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Tag toggle handler - now supporting multiple tags
  const handleTagToggle = (tag) => {
    if (activeFilters.includes(tag)) {
      // Remove the tag
      const newFilters = activeFilters.filter(t => t !== tag);
      setActiveFilters(newFilters);
    } else {
      // Check if we've reached the limit
      if (activeFilters.length >= MAX_ACTIVE_FILTERS) {
        addToast(`You can only select up to ${MAX_ACTIVE_FILTERS} tags at once. Remove a tag to add a new one.`, 'warning');
        return;
      }
      // Add the tag to the front of the array, removing any existing instance
      setActiveFilters(prevActive => [tag, ...prevActive.filter(t => t !== tag)]);
    }
  };

  // Clear all filters
  const clearAllFilters = () => {
    setActiveFilters([]);
  };

  // Sort options
  const sortOptions = [
    { value: 'random', label: 'Randomize', icon: Shuffle },
    { value: 'newest', label: 'Newly added', icon: Clock }
  ];

  const currentSortOption = sortOptions.find(option => option.value === sortMethod) || sortOptions[0];

  return (
    <div className="mb-6">
      <div ref={filtersContainerRef} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        {/* Tags Section */}
        <div ref={tagsContainerRef} className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
          {/* Visible Tags */}
          {visibleTags.map((tag) => (
            <button
              key={tag}
              onClick={() => handleTagToggle(tag)}
              className={`
                px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium flex-shrink-0
                transition-colors duration-150 ease-in-out my-1
                ${activeFilters.includes(tag)
                  ? 'bg-purple-100 text-purple-800 hover:bg-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:hover:bg-purple-900/50 ring-1 ring-purple-300 dark:ring-purple-700'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                }
              `}
            >
              {tag}
            </button>
          ))}

          {/* Categories Button (Mobile/Tablet) - only show when no visible tags */}
          {showCategoriesButton && visibleTags.length === 0 && (
            <div className="relative">
              <button
                ref={categoriesMenuRef}
                onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium flex-shrink-0 transition-colors duration-150 ease-in-out my-1 ${
                  isMoreMenuOpen || activeFilters.length > 0
                    ? 'bg-purple-100 text-purple-800 hover:bg-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:hover:bg-purple-900/50 ring-1 ring-purple-300 dark:ring-purple-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                Formats
                {activeFilters.length > 0 && (
                  <span className="bg-white bg-opacity-20 text-xs px-1.5 py-0.5 rounded-full">
                    {activeFilters.length}
                  </span>
                )}
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* More Button (Desktop) - show when there are hidden tags and not showing categories button */}
          {(!showCategoriesButton || visibleTags.length > 0) && hiddenTags.length > 0 && (
            <div className="relative">
              <button
                ref={moreMenuRef}
                onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium flex-shrink-0 transition-colors duration-150 ease-in-out my-1 ${
                  isMoreMenuOpen
                    ? 'bg-purple-100 text-purple-800 hover:bg-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:hover:bg-purple-900/50 ring-1 ring-purple-300 dark:ring-purple-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                More
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Clear All Button */}
          {activeFilters.length > 0 && (
            <button
              onClick={clearAllFilters}
              className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            >
              <X className="w-3 h-3" />
              Clear
            </button>
          )}
        </div>

        {/* Clear Filters button - show when multiple filters are active */}
        {activeFilters.length > 1 && (
          <div className="flex items-center justify-start sm:justify-center mt-3 mb-3 text-sm text-gray-600 dark:text-gray-400 px-1">
            <button 
              onClick={clearAllFilters}
              className="flex items-center gap-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition-colors"
            >
              <X className="h-4 w-4" />
              Clear Filters
            </button>
          </div>
        )}

        {/* Controls Section */}
        <div ref={controlsRef} className="flex items-center gap-2 pl-2 my-1">
          {/* Saved filter */}
          {isLoggedIn && (
            <button
              onClick={() => setShowSavedVideoTemplates && setShowSavedVideoTemplates(!showSavedVideoTemplates)}
              className={`
                flex items-center justify-center gap-1 px-3 sm:px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium border
                ${showSavedVideoTemplates
                  ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white border-purple-500 dark:border-purple-600'
                  : 'bg-white text-gray-700 border-gray-200 hover:bg-purple-50 hover:text-purple-600 hover:border-purple-200 dark:bg-gray-900 dark:text-gray-300 dark:border-gray-800 dark:hover:bg-purple-900/20 dark:hover:text-purple-400 dark:hover:border-purple-800'}
              `}
            >
              <Star className={`h-4 w-4 ${showSavedVideoTemplates ? 'fill-white' : ''}`} />
              <span className="hidden xs:inline">Saved</span>
            </button>
          )}

          {/* Sort dropdown */}
          <div 
            ref={sortMenuRef} 
            className="flex items-center relative"
          >
            <button
              onClick={() => setIsSortMenuOpen(!isSortMenuOpen)}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-full hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors whitespace-nowrap"
            >
              <currentSortOption.icon className="w-4 h-4" />
              {currentSortOption.label}
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Dropdown Menus */}
      {isMoreMenuOpen && (
        <div
          ref={dropdownRef}
          className="fixed z-50 mt-2 w-56 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-800 max-h-[300px] overflow-y-auto"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
          }}
        >
          <div className="py-2 px-2">
            <div className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Video Formats
            </div>
            {(showCategoriesButton && visibleTags.length === 0 ? VIDEO_TEMPLATE_FORMAT_LIST : hiddenTags).map((tag) => (
              <button
                key={tag}
                onClick={() => {
                  handleTagToggle(tag);
                  setIsMoreMenuOpen(false);
                }}
                className={`
                  w-full text-left px-3 py-2 rounded-md text-sm mb-1 flex justify-between items-center transition-colors
                  ${activeFilters.includes(tag)
                    ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                    : 'hover:bg-gray-100 text-gray-700 dark:text-gray-300 dark:hover:bg-gray-700'
                  }
                `}
              >
                <span>{tag}</span>
                {activeFilters.includes(tag) && (
                  <span className="ml-2 text-purple-600 dark:text-purple-400">✓</span>
                )}
              </button>
            ))}
            {activeFilters.length > 0 && (
              <>
                <div className="border-t border-gray-200 dark:border-gray-600 my-2"></div>
                <button
                  onClick={() => {
                    clearAllFilters();
                    setIsMoreMenuOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 rounded-md text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  Clear all filters
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {isSortMenuOpen && (
        <div
          ref={sortDropdownRef}
          className="fixed z-50 mt-2 w-40 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700"
          style={{
            top: `${sortDropdownPosition.top}px`,
            left: `${sortDropdownPosition.left}px`,
          }}
        >
          <div className="py-1">
            {sortOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  setSortMethod(option.value);
                  setIsSortMenuOpen(false);
                }}
                className={`flex items-center gap-2 w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                  sortMethod === option.value
                    ? 'bg-primary-50 text-primary-600 dark:bg-primary-900 dark:text-primary-300'
                    : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                <option.icon className="w-4 h-4" />
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default VideoTemplateFilters; 